/**
 * Canvas 2D rendering helpers with retina support and Peel-style effects.
 */

/**
 * Set up a canvas element for retina-aware rendering.
 * Returns the 2D context scaled appropriately.
 */
export function setupCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): CanvasRenderingContext2D {
  const dpr = window.devicePixelRatio || 1;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return ctx;
}

/**
 * Draw a rounded rectangle (useful for bar charts).
 */
export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius = 4
) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Draw a rounded rectangle with a vertical gradient fill + optional glow.
 * Peel-style: gradient from solid at top to faded at bottom, subtle glow shadow.
 */
export function roundedRectGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  color: string,
  colorLight: string,
  radius = 4,
  glow = false
) {
  // Skip zero-height bars (value === 0)
  if (height <= 0 || width <= 0) return;

  // Glow shadow behind the bar
  if (glow && height > 8) {
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    roundedRect(ctx, x, y, width, height, radius);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.15;
    ctx.fill();
    ctx.restore();
  }

  // Gradient fill (guard for test environments with mock canvas)
  roundedRect(ctx, x, y, width, height, radius);
  if (typeof ctx.createLinearGradient === 'function') {
    const grad = ctx.createLinearGradient(0, y, 0, y + height);
    grad.addColorStop(0, color);
    grad.addColorStop(1, colorLight);
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = color;
  }
  ctx.fill();
}

/**
 * Draw grid lines on a canvas — Peel-style soft, barely visible lines.
 */
export function drawGridLines(
  ctx: CanvasRenderingContext2D,
  ticks: number[],
  scaleY: (v: number) => number,
  xStart: number,
  xEnd: number,
  color: string
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  for (const tick of ticks) {
    const y = Math.round(scaleY(tick)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(xStart, y);
    ctx.lineTo(xEnd, y);
    ctx.stroke();
  }
}

/**
 * Draw Y axis labels on a canvas.
 */
export function drawYAxisLabels(
  ctx: CanvasRenderingContext2D,
  ticks: number[],
  scaleY: (v: number) => number,
  x: number,
  color: string,
  formatFn: (v: number) => string
) {
  ctx.fillStyle = color;
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (const tick of ticks) {
    ctx.fillText(formatFn(tick), x, scaleY(tick));
  }
}

/**
 * Draw X axis labels on a canvas (supports rotation and label thinning).
 *
 * When `maxLabels` is provided and the number of labels exceeds it, labels
 * are evenly thinned (skipped) to prevent overlap — the first and last labels
 * are always drawn.
 */
export function drawXAxisLabels(
  ctx: CanvasRenderingContext2D,
  labels: string[],
  positions: number[],
  y: number,
  color: string,
  rotate = false,
  maxLabels?: number
) {
  ctx.fillStyle = color;
  ctx.font = '11px system-ui, sans-serif';

  // Determine step to thin labels when too many for the available space
  const step =
    maxLabels && maxLabels > 0 && labels.length > maxLabels
      ? Math.ceil(labels.length / maxLabels)
      : 1;

  for (let i = 0; i < labels.length; i++) {
    // Skip labels that would overlap (always show first and last)
    if (step > 1 && i % step !== 0 && i !== labels.length - 1) continue;

    const x = positions[i]!;
    const label = labels[i]!;

    ctx.save();
    if (rotate) {
      ctx.translate(x, y);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(label.length > 28 ? label.slice(0, 26) + '\u2026' : label, 0, 0);
    } else {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(label.length > 20 ? label.slice(0, 18) + '\u2026' : label, x, y);
    }
    ctx.restore();
  }
}

/**
 * Draw an area fill under a line — Peel-style gradient from color to transparent.
 */
export function drawAreaFill(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
  baseline: number,
  color: string,
  topAlpha = 0.25,
  bottomAlpha = 0.02
) {
  if (points.length < 2) return;

  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  ctx.beginPath();
  ctx.moveTo(points[0]!.x, baseline);
  for (const p of points) ctx.lineTo(p.x, p.y);
  ctx.lineTo(points[points.length - 1]!.x, baseline);
  ctx.closePath();

  // Guard for test environments with mock canvas
  if (typeof ctx.createLinearGradient === 'function') {
    const grad = ctx.createLinearGradient(0, points[0]!.y, 0, baseline);
    grad.addColorStop(0, `rgba(${r},${g},${b},${topAlpha})`);
    grad.addColorStop(0.5, `rgba(${r},${g},${b},${topAlpha * 0.3})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},${bottomAlpha})`);
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = `rgba(${r},${g},${b},${topAlpha})`;
  }
  ctx.fill();
}
